import { getToken } from '~gatsby-theme-autonomy/redux/actions/auth'
import axiosClient from './axios-client'
import qs from 'query-string'

class Api {
  async getAuthorizationHeader() {
    if (!axiosClient.store) return null

    const { auth } = axiosClient.store.getState()
    // if there is a current auth token, use it
    if (auth.token) return auth.token

    // if there is not a current auth token, but the user is logged in, retrieve an auth token
    try {
      if (auth.isLoggedIn) await axiosClient.store.dispatch(getToken())
      const { auth: updatedAuth } = axiosClient.store.getState()
      if (updatedAuth.token) return updatedAuth.token
    } catch (error) {}

    return null
  }

  async findOrCreateCart() {
    let cart = await this.fetchCurrentCart()
    if (!cart) {
      cart = await this.createCart()
    }
    return cart
  }

  async createCart() {
    const url = '/api/orders'
    const payload = { line_items_attributes: [] }
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.post(url, payload, config)
    return response.data
  }

  async fetchCart(number) {
    const url = `/api/orders/${number}`
    const response = await axiosClient.get(url)
    return response.data
  }

  async fetchCurrentCart() {
    const url = `/api/orders/current`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.get(url, config)
    return response.data
  }

  async updateCart(cart, props) {
    const url = `/api/orders/${cart.number}`
    const response = await axiosClient.patch(url, { id: cart.id, ...props })
    return response.data
  }

  async addToCart(cart, sku, quantity) {
    const url = `/api/orders/${cart.number}/line_items`

    const payload = {
      line_item: {
        sku: sku,
        quantity: quantity,
      },
    }

    const response = await axiosClient.post(url, payload)
    return response.data
  }

  async removeFromCart(cart, lineItemId) {
    const url = `/api/orders/${cart.number}/line_items/${lineItemId}`
    return await axiosClient.delete(url)
  }

  async updateCartItem(cart, lineItemId, payload) {
    const url = `/api/orders/${cart.number}/line_items/${lineItemId}`
    const response = await axiosClient.patch(url, payload)
    return response.data
  }

  async updateGiftCards(cart, giftCardsDetails) {
    const url = `/api/orders/${cart.number}/gift_cards/bulk_update`
    const payload = {
      giftCards: giftCardsDetails,
    }
    const response = await axiosClient.patch(url, payload)
    return response.data
  }

  async addPromoCode(cart, promoCode) {
    const url = `/api/orders/${cart.number}/coupon_codes`
    const payload = { coupon_code: promoCode }
    const response = await axiosClient.post(url, payload)
    return response.data
  }

  async removePromoCode(cart, promoCode) {
    const url = `/api/orders/${cart.number}/coupon_codes/${promoCode}`
    const response = await axiosClient.delete(url)
    return response.data
  }

  async checkout(cart) {
    const url = `/api/checkouts/${cart.number}/prepare`
    const response = await axiosClient.put(url)
    return response.data
  }

  async getStates(iso) {
    const url = `/api/countries/${iso}/states`
    const config = { public: true }
    const response = await axiosClient.get(url, config)
    return response.data
  }

  async getUserWallet() {
    const url = `/api/wallet_payment_sources`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.get(url, config)
    return response.data
  }

  async updateOrderAddresses(cart, shipAddressAttributes) {
    const url = `/api/checkouts/${cart.number}`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const payload = {
      order: {
        bill_address_attributes: shipAddressAttributes,
        use_billing: true,
      },
      state: 'address',
    }

    const response = await axiosClient.patch(url, payload, config)
    return response.data
  }

  async updateOrderDelivery(cart) {
    const url = `/api/checkouts/${cart.number}`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    // Since there is only one available shipping method in Chord OMS,
    // to update the delivery, we can just send an empty field
    // (otherwise the API returns an error because the order field is required),
    // and Solidus will automatically set the available shipping method
    const payload = {
      order: {
        shipments_attributes: null,
      },
      state: 'delivery',
    }

    const response = await axiosClient.patch(url, payload, config)
    return response.data
  }

  async updateOrderPayment(cart, paymentAttributes) {
    const url = `/api/checkouts/${cart.number}`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const payload = {
      order: {
        payments_attributes: [paymentAttributes],
      },
      state: 'payment',
    }

    const response = await axiosClient.patch(url, payload, config)
    return response.data
  }

  async finalizeCheckout({ orderNumber, checkoutSessionId = null }) {
    const url = `/api/checkouts/${orderNumber}/finalize`
    const payload = checkoutSessionId
      ? { checkout_session_id: checkoutSessionId }
      : {}
    const response = await axiosClient.put(url, payload)
    return response.data
  }

  async fetchUserReferralIdentifier(email) {
    const url = '/api/referral_identifiers'
    const payload = { email }
    const response = await axiosClient.post(url, payload, {
      transformRequest: [
        (data, headers) => {
          delete headers['X-Spree-Order-Token']
          return JSON.stringify(data)
        },
      ],
    })
    return response.data
  }

  async subscribeProduct(cart, { sku, quantity, interval, endDate = {} }) {
    const url = `/api/orders/${cart.number}/line_items`

    const payload = {
      line_item: {
        sku: sku,
        quantity: quantity,
      },
      subscription_line_item: {
        interval_length: interval.length,
        interval_units: interval.unit[0],
        end_date: endDate,
      },
    }

    const response = await axiosClient.post(url, payload)
    return response.data
  }

  async associateUser(cart) {
    const url = `/api/orders/${cart.number}/associate_user?order_token=${cart.token}`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.put(url, null, config)
    return response.data
  }

  async fetchUser() {
    const url = `/api/users/me`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.get(url, config)
    return response.data
  }

  async updateUser(id, props) {
    const url = `/api/users/${id}`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.patch(url, props, config)
    return response.data
  }

  async fetchSubscriptions(userId) {
    const url = `/api/users/${userId}/subscriptions`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.get(url, config)
    return response.data.subscriptions
  }

  async skipSubscription(userId, subscriptionId) {
    const url = `/api/users/${userId}/subscriptions/${subscriptionId}/skip`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.post(url, null, config)
    return response.data
  }

  async loadSubscription(userId, subscriptionId) {
    const url = `/api/users/${userId}/subscriptions/${subscriptionId}`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.get(url, config)
    return response.data
  }

  async cancelSubscription(userId, subscriptionId) {
    const url = `/api/users/${userId}/subscriptions/${subscriptionId}/cancel`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.post(url, null, config)
    return response.data
  }

  async updateSubscription(userId, subscriptionId, props) {
    const url = `/api/users/${userId}/subscriptions/${subscriptionId}`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.put(url, props, config)
    return response.data
  }

  async resumeSubscription(userId, subscriptionId, props) {
    const url = `/api/users/${userId}/subscriptions/${subscriptionId}/resume`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.post(url, props, config)
    return response.data
  }

  async pauseSubscription(userId, subscriptionId, props) {
    const url = `/api/users/${userId}/subscriptions/${subscriptionId}/pause`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()
    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.post(url, props, config)
    return response.data
  }

  async getVariantsBySkus(skus) {
    const url = `/api/variants`
    const config = { headers: {} }

    const authToken = await this.getAuthorizationHeader()

    if (authToken) config.headers.Authorization = authToken

    const response = await axiosClient.get(url, {
      ...config,
      params: {
        'q[sku_in]': skus,
      },
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: 'bracket' }),
    })

    return response.data.variants
  }

  async createStockRequest({ email, sku }) {
    const url = `/api/stock_requests`
    const config = {
      headers: {},
      transformRequest: [
        (data, headers) => {
          delete headers['X-Spree-Order-Token']
          return JSON.stringify(data)
        },
      ],
    }

    const props = { stock_request: { email, sku } }

    const response = await axiosClient.post(url, props, config)
    return response.data
  }
}

const api = new Api()

export default api
